import { RECOVERY_STANDING_QUEUE_MS_ENTER } from '@playcastdotio/PeerCore';

/**
 * A minimize, restore, or maximize of the host window stalls the encoder. That stall is not
 * a network event. Recovery must not open a bitrate cut from it, and a cut that landed in
 * the same moment is put back when the link itself is not congested.
 */

export const HOST_WINDOW_TRANSITION_HOLD_MS = 4_000;
/** A cut that opened this recently can still be the window's stall, not a separate episode. */
export const HOST_WINDOW_CUT_UNDO_MS = 5_000;

const WINDOW_STATES = new Set(['minimized', 'normal', 'restored', 'maximized']);

export type HostWindowLinkSample = {
  lossPct?: number;
  standingQueueMs?: number | null;
  lossDelta?: number;
};

export function isHostWindowShowState(state: string): boolean {
  return WINDOW_STATES.has(state);
}

/** Loss or a real standing queue. A freeze or an fps dip alone is not this. */
export function linkProvesCongestion(sample: HostWindowLinkSample | undefined): boolean {
  if (!sample) return false;
  if ((sample.lossPct ?? 0) >= 2) return true;
  if ((sample.lossDelta ?? 0) > 50) return true;
  return typeof sample.standingQueueMs === 'number'
    && sample.standingQueueMs >= RECOVERY_STANDING_QUEUE_MS_ENTER;
}

export class HostWindowTransitionGuard {
  private holdUntilMs = 0;

  public note(nowMs: number): void {
    this.holdUntilMs = nowMs + HOST_WINDOW_TRANSITION_HOLD_MS;
  }

  public holding(nowMs: number): boolean {
    return nowMs < this.holdUntilMs;
  }

  public cutOpenedWithTransition(enteredAtMs: number, nowMs: number): boolean {
    return nowMs - enteredAtMs >= 0 && nowMs - enteredAtMs <= HOST_WINDOW_CUT_UNDO_MS;
  }
}

type WindowEventTarget = {
  on?: (channel: string, callback: (state: string) => void) => void;
  removeListener?: (channel: string, callback: (state: string) => void) => void;
};

/** Subscribe to the host window's show-state. Returns an unsubscribe. No-ops outside Electron. */
export function watchHostWindowTransitions(
  onTransition: () => void,
  electron: WindowEventTarget | undefined = (globalThis as { electron?: WindowEventTarget }).electron,
): () => void {
  if (!electron?.on) return () => undefined;
  const handler = (state: string) => {
    if (isHostWindowShowState(state)) onTransition();
  };
  electron.on('window-state-changed', handler);
  return () => electron.removeListener?.('window-state-changed', handler);
}
